import { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAppSelector } from "../../../hooks/hooks";
import useOnClickOutside from "../../../hooks/useOnClickOutside";
import Checkbox from "../../customize-button/components/form/Checkbox";
import {
  selectSecondaryFilter,
  updateSecondaryFilter,
} from "../secondaryFilterSlice";
import translate from "../../../utilities/translator";
import { scrollToResults } from "../../../utilities/helpers";
import { pushEvent } from "../../../utilities/analytics";
import { BOARD_TYPE_FILTER_UPDATE_EVENT, SEC_FILTER_OPEN } from "../../../constants/analyticEvents";
import { clearSelectedHotel } from "../../search/searchSlice";
import { filterClearClick, filterSaveClick, setLastChangedFilter } from '../../../appSlice';
import { clearPrefetchDeals } from '../../dealsList/prefetchDealsSlice';
import useUserPreferences from '../../../hooks/preferences/useUserPreferences';

interface BoardTypeProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export default function BoardType({ isOpen, setIsOpen }: BoardTypeProps) {
  const { data } = useSelector(selectSecondaryFilter);
  const { board } = data;
  const name = translate('holidayFinder.secondaryFilter.Board_type');
    const { user } = useAppSelector(state => state.user);
    const { updatePreference } = useUserPreferences(user?.muid || '')

  // remove Room only
  const [localState, setLocalState] = useState(board.filter(({ value }) => value !== 'ro'));
  const ref = useRef();
  const dispatch = useDispatch();

  useOnClickOutside(ref, () => isOpen && setIsOpen(false));

  const onSaveClick = (event) => {
    setIsOpen(false);
    dispatch(filterSaveClick());
    dispatch(clearPrefetchDeals());
    dispatch(updateSecondaryFilter({ updatedValue: localState, filter: 'board' }));
    dispatch(setLastChangedFilter('secondaryFilter'));
    event.stopPropagation();
    pushEvent(BOARD_TYPE_FILTER_UPDATE_EVENT, localState);
    dispatch(clearSelectedHotel());
    scrollToResults();
    clearPreferencesIfNeeded(); // default behavior on save
  };

  const onClearClick = (event) => {
    setIsOpen(false);
    dispatch(filterClearClick());
    const disable = board.map((item) => ({ ...item, active: false }));
    setLocalState(disable);
    dispatch(updateSecondaryFilter({ updatedValue: disable, filter: 'board' }));
    event?.stopPropagation();
    scrollToResults();
    pushEvent(BOARD_TYPE_FILTER_UPDATE_EVENT, disable);
    clearPreferencesIfNeeded(true); // force clear preferences on clear click
  };

  const clearPreferencesIfNeeded = (forceClear = false) => {
    try {
      const bbOption = localState.find(({ value }) => value === 'bb');
      if (forceClear || (bbOption && !bbOption.active)) {
        updatePreference({ breakfast_quality: false });
      }
    } catch (error) {
      console.error('Error clearing preferences', error);
    }
  }

  useEffect(() => {
    setLocalState(board.filter(({ value }) => value !== 'ro'));
  }, [board]);

  const handleCheckbox = (index) => {
    setLocalState(localState.map((item, i) => (index === i ? { ...item, active: !item.active } : item)));
  };

  const openFilter = (st) => {
    if (!isOpen) {
      pushEvent(SEC_FILTER_OPEN, { filterName: 'board' });
    }
    setIsOpen(st);
  };

  const renderCheckbox = localState.map(({ value, label, active }, i) => (
    <Checkbox key={value} label={label} checked={active} onChange={() => handleCheckbox(i)} />
  ));

  const isActiveFilter = board.filter(({ active }) => active).length > 0;
  const activeFilters = board.filter(({ active }) => active);

  let activeName = '';
  if (isActiveFilter) {
    activeName = `${board.filter(({ active }) => active)[0].label} ${
      activeFilters.length > 1 ? `+ ${board.filter(({ active }) => active).length - 1}` : ''
    } `;
  }

  return (
    <div className="filter-item filter-item-v2 board-type" ref={ref}>
      <p
        className={`filter-item__name ${isOpen ? 'filter-item__name-open' : ''} ${
          isActiveFilter ? 'filter-item__name-active' : ''
        }`}
        onClick={() => openFilter(!isOpen)}
        role="menuitem"
        ref={ref}
        tabIndex="0"
      >
        <span>{isActiveFilter ? activeName : name}</span>
        {isActiveFilter ? (
          <i onClick={(event) => onClearClick(event)} className="ri-close-line close"></i>
        ) : (
          <i className="ri-arrow-down-s-line chevron"></i>
        )}
      </p>
      {isOpen && (
        <div className="filter-body filter-body-v2 left" style={{ minWidth: "340px" }}>
          <div className="filter-body-wrap flex flex-col gap-3">{renderCheckbox}</div>
          <div className="filter-footer-v2 flex gap-3">
            <button className="panel-button panel-button__clear py-4 px-6 rounded-full" onClick={onClearClick}>
              {translate('holidayFinder.buttons.Clear')}
            </button>
            <button className="panel-button panel-button__save py-4 px-6 rounded-full" onClick={onSaveClick}>
              {translate('holidayFinder.buttons.Save')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
